// Invalidate the infinite-staleTime dictation-context cache on a session
// create (patient switch) so a previous patient's terms can't bleed into
// another's dictation_context. Web mounts it from DictationBubble; desktop from
// useDesktopDictationHotkey, where it also re-syncs the native config.

// NOUN COLLISION, not session data. The name carries "Session" because the
// trigger is a session-create mutation, but the cache this hook invalidates is
// the DICTATION context and nothing here reads or writes session state. The
// ratchet's second signal — a `@tanstack/react-query` import — only appeared
// when the client stopped being imported as the app singleton and started being
// resolved from provider context, which is the ruled shape for this crossing.
// eslint-disable-next-line @heidi/rules/no-new-legacy-session-data -- dictation cache invalidated on a session-create trigger; see above
import { isTauri } from '@heidi/runtime/platform';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { getDictationBridgePort } from '../../../../dictation-bridge-ports';
import { getDictationBubblePorts } from '../../bubble-ports';
import {
  invalidateDictationContext,
  resolveDictationContext,
} from '../../store/getDictationContext';

export const useInvalidateDictationContextOnSessionCreate = (enabled: boolean): void => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) return;

    const mutationCache = queryClient.getMutationCache();
    return mutationCache.subscribe((event) => {
      const ports = getDictationBubblePorts();
      if (!ports.isDictationContextEnabled()) return;
      if (event.type !== 'updated' || event.action.type !== 'success') return;

      const mutationKey = event.mutation.options.mutationKey?.[0];
      if (!ports.isSessionCreateMutationKey(mutationKey)) return;

      invalidateDictationContext();

      if (isTauri()) {
        void getDictationBridgePort().updateNativeDictationConfig();
      } else {
        void resolveDictationContext();
      }
    });
  }, [enabled, queryClient]);
};
